import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import {
  getIdToken,
  getRefreshToken,
  getStoredUser,
  storeSession,
  clearSession,
  subscribeToSession,
} from '../utils/authStorage';

const AuthContext = createContext(null);

const initialState = {
  user: null,
  idToken: null,
  refreshToken: null,
  initialized: false,
};

export function AuthProvider({ children }) {
  const [state, setState] = useState(initialState);
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    const winUser = getStoredUser();
    const token = getIdToken();
    const refresh = getRefreshToken();

    setState({
      user: winUser,
      idToken: token,
      refreshToken: refresh,
      initialized: true,
    });

    const unsubscribe = subscribeToSession((detail) => {
      if (detail.cleared) {
        setState((prev) => ({
          ...prev,
          user: null,
          idToken: null,
          refreshToken: null,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        user: detail.user || prev.user,
        idToken: detail.idToken || prev.idToken,
        refreshToken: detail.refreshToken || prev.refreshToken,
      }));
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { idToken, refreshToken, user } = response.data;

      storeSession({ idToken, refreshToken, user });
      setState({
        user,
        idToken,
        refreshToken,
        initialized: true,
      });

      return response.data;
    } catch (error) {
      console.error('❌ Login failed:', error);
      const message =
        error.response?.data?.error ||
        error.message ||
        'Login failed. Please try again.';
      setAuthError(message);
      throw new Error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const register = async ({ email, password, displayName }) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        displayName: displayName || undefined
      });
      const { idToken, refreshToken, user } = response.data;

      if (!idToken) {
        const createdError = new Error(response.data?.error || 'Account created. Please sign in.');
        createdError.accountCreated = true;
        throw createdError;
      }

      storeSession({ idToken, refreshToken, user });
      setState({
        user,
        idToken,
        refreshToken,
        initialized: true,
      });

      return response.data;
    } catch (error) {
      console.error('❌ Registration failed:', error);
      const message =
        error.response?.data?.error ||
        error.message ||
        'Registration failed. Please try again.';
      if (error.accountCreated) throw error;
      setAuthError(message);
      throw new Error(message);
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.warn('Logout request failed:', error.response?.data || error.message);
    } finally {
      clearSession();
      setState({
        user: null,
        idToken: null,
        refreshToken: null,
        initialized: true,
      });
    }
  };

  const value = useMemo(
    () => ({
      user: state.user,
      idToken: state.idToken,
      refreshToken: state.refreshToken,
      initialized: state.initialized,
      isAuthenticated: Boolean(state.user && state.idToken),
      login,
      register,
      logout,
      authError,
      authLoading,
      clearAuthError: () => setAuthError(null),
    }),
    [state, authError, authLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}

